import { useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { paymentNotificationService } from '../lib/services/payment/paymentNotificationService';
import { type PaymentNotificationListenerOptions } from '../types/payment';
import { logger } from '../lib/core/env';

export const usePaymentNotifications = (
  options: PaymentNotificationListenerOptions = {},
  onPaymentStatusChange?: (oldStatus: string, newStatus: string) => void
) => {
  const { user, isAuthenticated } = useAuthStore();
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Only start listening if user is authenticated and has a valid ID
    if (isAuthenticated && user?.id) {
      // Prevent multiple initializations
      if (!isInitializedRef.current) {
        logger.debug('Initializing payment notification listener for user:', user.id);
        isInitializedRef.current = true;
      }

      // Start listening for payment notifications
      paymentNotificationService.startListening(user.id, options, onPaymentStatusChange);
    } else if (!isAuthenticated || !user?.id) {
      // Stop listening if user is not authenticated or has no ID
      if (isInitializedRef.current) {
        logger.debug('Stopping payment notification listener - user not authenticated');
        paymentNotificationService.stopListening();
        isInitializedRef.current = false;
      }
    }
  }, [isAuthenticated, user?.id, JSON.stringify(options)]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isInitializedRef.current) {
        logger.debug('Cleaning up payment notification listener on unmount');
        paymentNotificationService.stopListening();
        isInitializedRef.current = false;
      }
    };
  }, []);

  // Return service methods for manual control if needed
  return {
    isListening: paymentNotificationService.isCurrentlyListening(),
    currentUserId: paymentNotificationService.getCurrentUserId(),
    startListening: (userId: string, opts?: PaymentNotificationListenerOptions) => {
      paymentNotificationService.startListening(userId, opts, onPaymentStatusChange);
    },
    stopListening: () => {
      paymentNotificationService.stopListening();
    },
  };
};
